import { ReactElement, useMemo } from 'react'
import { BasicMesh, ImmutableVector3 } from '@modugen/ui-toolkit'
import { Line } from '@react-three/drei'
import { useAppStore } from '@stores'
import { loadCaseLabels } from 'src/components/pages/Editor/utils/loadTittles'
import TextSprite from 'src/components/scene/TextSprite'
import { getLoadCategoryColor } from 'src/components/scene/loads'
import { loadPointOnDomain } from 'src/components/scene/utils'
import { getTextColorOnWhite } from 'src/utils/color'
import { formatCompactTwoDecimals } from 'src/utils/numberFormatters'
import {
  LABEL_BORDER_WIDTH,
  LABEL_GAP,
  TORUS_TUBE_RADIUS,
  labelDeltaY,
} from '../FloorplanLineLoadsMesh/utils'
import { pointLoadMagnitude, pointLoadRadius } from './utils'

interface Props {
  loads: PointLoadWithDomain[]
  /** Largest point load value of the project; all circles scale proportionally to it. */
  maxMagnitude: number
}

interface LoadAtPosition {
  position: ImmutableVector3
  loads: Array<{
    pointLoad: PointLoadWithDomain
    loadLabel: string
    loadCaseTypeShort: string
    loadColor: string
    textColor: string
  }>
}

const FloorplanPointLoadsMesh = ({ loads, maxMagnitude }: Props): ReactElement => {
  const maxDiameter = useAppStore(state => state.loadRenderConfig.pointLoads.floorplanMaxDiameter)
  const minDiameter = useAppStore(state => state.loadRenderConfig.pointLoads.floorplanMinDiameter)
  const labelScale = useAppStore(state => state.loadRenderConfig.pointLoads.floorplanTextScale)

  // Group loads by position (rounded to 2 decimal places)
  const loadsGroupedByPosition = useMemo(() => {
    const positionMap = new Map<string, LoadAtPosition>()

    loads.forEach(pointLoad => {
      const { domain } = pointLoad

      if (!domain) return

      // Calculate the 3D position of the point load on the domain
      const point = loadPointOnDomain(pointLoad.relative_position, domain.start, domain.end)
      const position = new ImmutableVector3(point.x, point.y, point.z)

      // Create position key with 2 decimal places
      const positionKey = `${position.x.toFixed(2)},${position.y.toFixed(2)}`

      // Format load magnitude
      const positiveForce = pointLoad.force.z * -1
      const formattedLoad = formatCompactTwoDecimals(positiveForce)
      const loadLabel = `${formattedLoad}N`

      // Get load case type abbreviation
      const loadCaseTypeShort =
        loadCaseLabels[pointLoad.load_case.category][
          pointLoad.load_case.load_case_type as LoadCaseTypes
        ]?.short || '-'

      const loadColor = getLoadCategoryColor(pointLoad.load_case.category)
      const textColor = getTextColorOnWhite(loadColor)

      if (!positionMap.has(positionKey)) {
        positionMap.set(positionKey, {
          position,
          loads: [],
        })
      }

      positionMap.get(positionKey)!.loads.push({
        pointLoad,
        loadLabel,
        loadCaseTypeShort,
        loadColor,
        textColor,
      })
    })

    // Sort loads at each position by magnitude (descending)
    const groupedLoads = Array.from(positionMap.values())
    groupedLoads.forEach(loadAtPosition => {
      loadAtPosition.loads.sort((a, b) => {
        const magnitudeA = Math.abs(a.pointLoad.force.z)
        const magnitudeB = Math.abs(b.pointLoad.force.z)
        return magnitudeB - magnitudeA // Descending order (largest first)
      })
    })

    return groupedLoads
  }, [loads])

  return (
    <>
      {loadsGroupedByPosition.map((loadAtPosition, index) => {
        const { position, loads } = loadAtPosition

        // Use the first load's color for the torus and diagonal lines
        const firstLoad = loads[0]
        const loadColor = firstLoad.loadColor
        // sized by the largest load at this position
        const torusRadius = pointLoadRadius(pointLoadMagnitude(firstLoad.pointLoad), {
          maxMagnitude,
          maxDiameter,
          minDiameter,
        })

        // Calculate diagonal line endpoints
        const diagonalOffset = (torusRadius * Math.sqrt(2)) / 2

        // Line 1: from bottom-left to top-right
        const line1Start = new ImmutableVector3(
          position.x - diagonalOffset,
          position.y - diagonalOffset,
          position.z,
        )
        const line1End = new ImmutableVector3(
          position.x + diagonalOffset,
          position.y + diagonalOffset,
          position.z,
        )

        // Line 2: from bottom-right to top-left
        const line2Start = new ImmutableVector3(
          position.x + diagonalOffset,
          position.y - diagonalOffset,
          position.z,
        )
        const line2End = new ImmutableVector3(
          position.x - diagonalOffset,
          position.y + diagonalOffset,
          position.z,
        )

        const diagonalLines = [
          [line1Start, line1End],
          [line2Start, line2End],
        ]

        // Calculate label positions stacked vertically
        const labelOffset = torusRadius + LABEL_GAP // 2cm below the torus edge
        const baseLabelY = position.y - labelOffset

        return (
          <group key={index} position={[0, 0, 0.0001]}>
            {/* Marker above the labels, so the labels of nearby loads don't cover it */}
            <group position={[0, 0, 0.01]}>
              {/* Torus geometry */}
              <BasicMesh position={[position.x, position.y, position.z]}>
                <torusGeometry args={[torusRadius, TORUS_TUBE_RADIUS, 16, 32]} />
                <meshStandardMaterial
                  color={loadColor}
                  opacity={0.75}
                  transparent={true}
                  depthWrite={false}
                />
              </BasicMesh>

              {/* Diagonal crossing lines */}
              <group position={[0, 0, 0.001]}>
                {diagonalLines.map((line, lineIndex) => (
                  <Line
                    key={lineIndex}
                    points={line.map(p => p.v)}
                    color={loadColor}
                    lineWidth={1.5}
                    segments
                    opacity={0.75}
                    dashed={false}
                    userData={{
                      type: 'point-load',
                      contextMenuActions: {
                        hide: {
                          guid: firstLoad.pointLoad.guid,
                        },
                        properties: {
                          guid: firstLoad.pointLoad.guid,
                        },
                      },
                    }}
                  />
                ))}
              </group>
            </group>

            {/* Stacked labels */}
            {loads.map((load, loadIndex) => {
              const labelY = baseLabelY - loadIndex * labelDeltaY(labelScale)
              const labelPosition = new ImmutableVector3(position.x, labelY, position.z)

              return (
                <TextSprite
                  key={loadIndex}
                  textLines={[`${load.loadLabel} ${load.loadCaseTypeShort}`]}
                  color={load.textColor}
                  backgroundColor="#ffffff"
                  borderColor={load.loadColor}
                  borderWidth={LABEL_BORDER_WIDTH}
                  position={labelPosition.v}
                  anchorLocation="top"
                  scale={labelScale}
                  scaleAxis="Y"
                  opacity={1.0}
                />
              )
            })}
          </group>
        )
      })}
    </>
  )
}

export default FloorplanPointLoadsMesh
